import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';

import { nombreParaMostrar, semillaDeAvatar } from '@/lib/sin-nombre';
import { cn, hashBucket, initials } from '@/lib/utils';

const avatarVariants = cva('relative flex shrink-0 overflow-hidden rounded-full', {
  variants: {
    size: {
      xs: 'size-5 text-[10px]',
      sm: 'size-6 text-[11px]',
      default: 'size-8 text-xs',
      lg: 'size-10 text-sm',
    },
  },
  defaultVariants: { size: 'default' },
});

export type AvatarSize = NonNullable<VariantProps<typeof avatarVariants>['size']>;

export type AvatarProps = React.ComponentProps<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarVariants>;

export function Avatar({ className, size, ...props }: AvatarProps) {
  return <AvatarPrimitive.Root className={cn(avatarVariants({ size }), className)} {...props} />;
}

export function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image className={cn('aspect-square size-full object-cover', className)} {...props} />
  );
}

export function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      className={cn(
        'flex size-full items-center justify-center rounded-full bg-muted font-medium text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The chart ramp is the only categorical palette in the token set, so it is
 * what gives each person a stable colour instead of a literal hex.
 */
const FALLBACK_TINTS = [
  'bg-chart-1/10 text-chart-1',
  'bg-chart-2/10 text-chart-2',
  'bg-chart-3/10 text-chart-3',
  'bg-chart-4/10 text-chart-4',
  'bg-chart-5/10 text-chart-5',
  'bg-chart-6/10 text-chart-6',
  'bg-chart-7/10 text-chart-7',
  'bg-chart-8/10 text-chart-8',
];

export interface UserAvatarProps
  extends Omit<React.ComponentProps<typeof AvatarPrimitive.Root>, 'children'> {
  /**
   * El nombre real, crudo. Vacío cuando la persona no lo dio: el hueco lo
   * dibuja este componente, no quien lo llama (ver `@/lib/sin-nombre`).
   */
  name: string;
  src?: string;
  size?: AvatarSize;
  /**
   * El id de la ficha, para el color cuando no hay nombre.
   *
   * `hashBucket('')` es una constante, así que sin esto TODAS las personas sin
   * nombre reciben el mismo tono: doce filas con el mismo «?» del mismo color
   * son indistinguibles de un vistazo. Con el id cada una recibe el suyo y
   * sigue siendo estable entre sesiones. Ver `semillaDeAvatar`.
   */
  seed?: string;
}

export function UserAvatar({ name, src, size, className, seed, ...props }: UserAvatarProps) {
  // El rótulo, no el dato: `aria-label=""` dejaba a quien navega con lector de
  // pantalla sin nada donde el resto ve un hueco. `initials('')` ya devuelve
  // «?», que es el hueco dibujado, y por eso sigue leyendo el nombre crudo.
  const rotulo = nombreParaMostrar(name);
  const tint = FALLBACK_TINTS[hashBucket(semillaDeAvatar(name, seed), FALLBACK_TINTS.length)] ?? '';
  return (
    <Avatar size={size} className={className} {...props}>
      {src ? <AvatarImage src={src} alt={rotulo} /> : null}
      <AvatarFallback aria-label={rotulo} className={tint}>
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  );
}
